import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  FillPlateSourceChipStructuredTableColumn,
  FillPlateSourceContainerChipStructuredTableColumn,
  SchemaAndColumnGroup,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {FrozenEntityChipData} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {SchematizedDisplayLayerMetadata} from 'samples/plate-maps/PlateMapConstants';
import BaseDetailTransferStructuredTableAdapter from 'samples/plate-maps/transfer-table/BaseDetailTransferStructuredTableAdapter';
import {BaseFillPlateTransferStructuredTableMetadata} from 'samples/plate-maps/transfer-table/BaseFillPlateTransferStructuredTableAdapter';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

export interface PlateRecordDetailStructuredTableMetadata extends BaseFillPlateTransferStructuredTableMetadata {
  frozenEntityChipData: {[id: string]: FrozenEntityChipData};
  plateDataParameterMetadata: Array<SchematizedDisplayLayerMetadata>;
  wellSchemaFieldMetadata: Array<SchematizedDisplayLayerMetadata>;
}

/**
 * Adapter describing and constraining the behavior of the Plate Record Detail Table in the Plate Record
 * Detail modal.
 *
 * This table is never embedded in Notebook and never persisted. Its content is immutable and is read from a
 * serialized record of the transfer that was performed.
 */
export default class PlateRecordDetailStructuredTableAdapter extends BaseDetailTransferStructuredTableAdapter<PlateRecordDetailStructuredTableMetadata> {
  static getAdapter(): PlateRecordDetailStructuredTableAdapter {
    return getStructuredTableAdapters()
      .FILL_PLATE_TRANSFER_PLATE_RECORD as PlateRecordDetailStructuredTableAdapter;
  }

  override _getAllSupportedColumns(params: {
    entryId: string | null;
    metadata: PlateRecordDetailStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const detailViewColumns = super._getAllSupportedColumns(params);
    const sourceContainerChipColumn: FillPlateSourceContainerChipStructuredTableColumn = {
      ...makeStructuredTableColumn({
        name: 'Source container',
        columnType: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP,
        isReadOnly: true,
        width: 300,
      }),
      columnType: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP, // need to re-specify to type narrow
      frozenEntityChipData: params.metadata.frozenEntityChipData,
      reagentGroupsById: params.metadata.reagentGroupsById,
      testOrderInfoById: params.metadata.testOrderInfoById,
    };
    const sourceChipColumn: FillPlateSourceChipStructuredTableColumn = {
      ...detailViewColumns[2],
      columnType: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP, // need to re-specify to type narrow
      frozenEntityChipData: params.metadata.frozenEntityChipData,
      reagentGroupsById: params.metadata.reagentGroupsById,
      testOrderInfoById: params.metadata.testOrderInfoById,
    };
    return [
      {
        ...detailViewColumns[0], // well
        isReadOnly: true,
      },
      makeStructuredTableColumn({
        name: 'Role',
        columnType: TableColumnType.EXPERIMENTAL_ROLE,
        isReadOnly: true,
      }),
      sourceContainerChipColumn,
      detailViewColumns[1], // icon
      sourceChipColumn,
      ...detailViewColumns.slice(3).map((column: StructuredTableColumn) => ({
        ...column,
        isReadOnly: true,
        // Override the source chip name to Content, since we're now looking at contents only.
        ...(column.columnType === TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP ? {name: 'Content'} : {}),
      })),
      ...params.metadata.wellSchemaFieldMetadata.map(({displayName}) =>
        makeStructuredTableColumn({
          name: displayName,
          columnType: TableColumnType.ANONYMOUS_TAG_VALUE,
          isReadOnly: true,
        })
      ),
      ...params.metadata.plateDataParameterMetadata.map(({displayName}) =>
        makeStructuredTableColumn({
          name: displayName,
          columnType: TableColumnType.ANONYMOUS_TAG_VALUE,
          isReadOnly: true,
        })
      ),
    ];
  }
}
